import { AlertsTable } from '@/components/Studies/Create/Steps/Alerts/AlertsTable';
import Button from '@/components/UI/Button';
import SelectDropDown from '@/components/UI/SelectDropDown';
import ActionList from '@/components/UI/SelectDropDown/Menus/ActionList';
import { _isEmpty } from '@/littledash';
import type { AnimalAlertSpecCreateOrUpdate } from '@/model/Alert.model';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { modalAction } from '@/utils/modal';
import type { CreateStudyState } from '../../Create.model';
import { type PresetCalculation } from '@/model/PresetCalculation.model.ts';
import { useStudyCreateStep } from '@/utils/studyCreation/useStudyCreate.tsx';
import { RouteService } from '@/support/RouteService';
import { alertsToMap, ensureAlertId, resolveCalculations } from '@/components/Studies/Create/Steps/Alerts/Alerts.utils';

interface AlertBulkActionsProps {
  selectedRows: Record<number, boolean>;
  handleDelete: () => void;
}

export const AlertBulkActions: FC<AlertBulkActionsProps> = ({ selectedRows, handleDelete }) => {
  const selectedRowCount = useMemo(() => Object.keys(selectedRows ?? {}).length, [selectedRows]);
  const hasSelectedRows = selectedRowCount > 0;

  const actions = [
    {
      name: 'Delete',
      key: 'delete',
      action: handleDelete,
    },
  ];
  return (
    <div className="flex items-center ml3">
      {hasSelectedRows && (
        <div
          className="mid-gray bg-light-gray ph3 ba b--moon-gray f6 br-0 br--left br1"
          style={{
            whiteSpace: 'nowrap',
            lineHeight: '2.4rem',
          }}
        >
          {selectedRowCount} selected
        </div>
      )}
      <SelectDropDown
        title="Bulk actions"
        className={`plain f6 ${hasSelectedRows ? 'br--right' : ''}`}
        alignMenu="right"
        disabled={!hasSelectedRows}
      >
        <ActionList actions={actions} />
      </SelectDropDown>
    </div>
  );
};

interface AlertsProps {
  state: CreateStudyState;
}

const Alerts: FC<AlertsProps> = ({ state }) => {
  const [calculations, setCalculations] = useState<Array<PresetCalculation>>([]);
  const { setStepReady } = useStudyCreateStep();
  const { openModal, closeModal } = modalAction(useDispatch());
  const { control } = useFormContext<{ alerts: Array<AnimalAlertSpecCreateOrUpdate> }>();
  const {
    field: { onChange: onAlertsChange, value: alerts },
  } = useController({
    name: 'alerts',
    control,
    defaultValue: [],
  });

  const [selectedRows, onSelectedRowsChange] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setCalculations(resolveCalculations(state));
  }, [state.study?.enable_consumption, state.form?.selectedPreset?.id, state.study?.preset_id, state.form?.presets]);

  const handleAddAlert = async (alert: AnimalAlertSpecCreateOrUpdate) => {
    const alertWithId = ensureAlertId(alert);
    const alertsMap = alertsToMap(alerts ?? []);
    alertsMap.set(alertWithId.id, alertWithId);
    onAlertsChange([...alertsMap.values()]);
    closeModal();
  };
  const handleDeleteAlert = async () => {
    onAlertsChange(alerts.filter((_alert, index) => !selectedRows?.[index]));
    onSelectedRowsChange({});
  };

  const addOrUpdateAlert = (alert?: AnimalAlertSpecCreateOrUpdate) => {
    openModal('ADD_ALERT_FORM', {
      calculations,
      alert,
      onSubmit: handleAddAlert,
      onCancel: () => closeModal(),
    });
  };

  useEffect(() => {
    setStepReady(true);
  }, [setStepReady]);

  return (
    <>
      <div className="mb4" data-testid="alerts__container">
        <h3 className="fw5 f5 pb2">Alerts</h3>
        <p className="f6 lh-copy ma0">
          Alerts are rules that can be set to alert users to a status change in an animal.{' '}
          <a
            href={RouteService.help('study_alerts')}
            target="_blank"
            rel="noopener noreferrer"
            className="dib link blue"
          >
            Read more.
          </a>
        </p>
      </div>
      <div className="ui-card">
        <div className="w-100 flex justify-between mt3">
          <AlertBulkActions selectedRows={selectedRows} handleDelete={handleDeleteAlert} />
          <Button
            className="relative mr3 br-pill"
            icon="add_new"
            paleBlue
            onClick={() => addOrUpdateAlert()}
            disabled={_isEmpty(calculations)}
          >
            <span>Add Alert</span>
          </Button>
        </div>
        <div>
          <AlertsTable
            alerts={alerts}
            calculations={calculations}
            onAlertClick={(alert) => addOrUpdateAlert(alert)}
            onAddAlertClick={() => addOrUpdateAlert()}
            selectedRows={selectedRows}
            onSelectedRowsChange={onSelectedRowsChange}
          />
        </div>
      </div>
    </>
  );
};

export default Alerts;
